import { useMemo, useState } from "react"
import { WarningCircle } from "@phosphor-icons/react"

import { Button } from "~/components/design-system/components"
import { usePermission } from "~/hooks/usePermission"
import { cn } from "~/utils/helpers"
import { barClearance } from "../DraftCommandbar"
import { isPackageOpen, PACKAGE_STATUS_META, packageWindowLabel } from "../packageDisplay"
import type { RangePackage } from "../types"
import { useReplenishmentRules, type StoreWithStatus } from "../useAllocationData"
import { PackageDraftCommandbar, PackageDraftProvider, usePackageDraft } from "./packageDraft"

/** Coverage matrix: one row per store, one column per (non-archived) product
 * package. A cell answers "what governs this pair today" — assignment, the
 * rule in force, and transit. Clicking a cell stages a toggle of the
 * assignment; staged toggles save together from the command bar. */
export default function SetupCoverage() {
  return (
    <PackageDraftProvider>
      <CoverageGrid />
    </PackageDraftProvider>
  )
}

function CoverageGrid() {
  const { rules } = useReplenishmentRules()
  const { allowed: canEdit } = usePermission("edit")
  const [gapsOnly, setGapsOnly] = useState(false)
  const draft = usePackageDraft()
  const packages = draft.packages
  const stores = draft.stores

  const visiblePackages = useMemo(
    () => packages.filter((pkg) => isPackageOpen(pkg.status)),
    [packages],
  )
  const ruleNameById = useMemo(() => new Map(rules.map((r) => [r.id, r.name])), [rules])

  const sortedStores = useMemo(() => {
    const bySource = [...stores].sort(
      (a, b) =>
        (a.warehouseId ?? "").localeCompare(b.warehouseId ?? "") ||
        a.storeNumber.localeCompare(b.storeNumber),
    )
    return bySource.filter((store) => {
      if (!gapsOnly) return true
      const hasPackage = visiblePackages.some(
        (pkg) => pkg.status === "active" && pkg.storeIds.includes(store.id),
      )
      return store.replenishmentRuleId === null || !hasPackage
    })
  }, [stores, gapsOnly, visiblePackages])

  const gapCount = useMemo(
    () =>
      stores.filter((store) => {
        const hasPackage = visiblePackages.some(
          (pkg) => pkg.status === "active" && pkg.storeIds.includes(store.id),
        )
        return store.replenishmentRuleId === null || !hasPackage
      }).length,
    [stores, visiblePackages],
  )

  const toggleAssignment = (store: StoreWithStatus, pkg: RangePackage) => {
    if (!canEdit || draft.saving || pkg.storesFolderId !== null) return
    draft.toggle(pkg.id, [store.id], !pkg.storeIds.includes(store.id))
  }

  if (draft.storesLoading || draft.packagesLoading) {
    return <div className="paragraph-sm p-6 text-fg-muted">Loading coverage…</div>
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-base bg-base">
      <div className="flex flex-wrap items-center gap-2.5 border-b border-base px-3.5 py-2.5">
        <span className="label-base">Coverage</span>
        <span className="paragraph-xs text-fg-muted">
          {stores.length} stores · {visiblePackages.length} product packages · {gapCount} store
          {gapCount === 1 ? "" : "s"} with gaps
        </span>
        <div className="flex-1" />
        <Button
          size="sm"
          variant={gapsOnly ? "neutral" : "transparent"}
          onClick={() => setGapsOnly((v) => !v)}
        >
          Gaps only
        </Button>
      </div>
      <div className="paragraph-xs flex items-start gap-2 border-b border-base bg-subtle px-3.5 py-2 text-fg-subtle">
        <span className="mt-1.5 size-1 shrink-0 rounded-full bg-fg-muted" />
        Packages decide whether a product may flow into a store today. Rules decide how much flows.
        Each cell is one store and one product package. Click it to toggle the assignment, then
        save.
      </div>
      <div className={cn("min-h-0 flex-1 overflow-auto", barClearance(draft.changes.length))}>
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="label-sm sticky top-0 left-0 z-30 min-w-32 border-r border-b border-base bg-base px-3 py-2 text-left text-fg-muted">
                Source location
              </th>
              <th className="label-sm sticky top-0 left-32 z-20 min-w-52 border-r border-b border-base bg-base px-3 py-2 text-left text-fg-muted">
                Store
              </th>
              {visiblePackages.map((pkg) => (
                <th
                  key={pkg.id}
                  className="sticky top-0 z-10 min-w-44 border-r border-b border-base bg-base px-3 py-1.5 text-left"
                >
                  <div className="flex items-center gap-1.5">
                    <span className="label-base truncate">{pkg.name}</span>
                    <span
                      className={cn(
                        "paragraph-xs whitespace-nowrap",
                        pkg.status === "active"
                          ? "text-badge-green-fg-text"
                          : "text-badge-blue-fg-text",
                      )}
                    >
                      {PACKAGE_STATUS_META[pkg.status].label}
                    </span>
                  </div>
                  <div className="paragraph-xs whitespace-nowrap text-fg-muted">
                    {pkg.type === "noos"
                      ? "NOOS · always open"
                      : packageWindowLabel(pkg.startDate, pkg.cutoffDate)}
                    {` · ${pkg.productIds.length} products`}
                  </div>
                </th>
              ))}
              {visiblePackages.length === 0 && (
                <th className="paragraph-xs sticky top-0 z-10 border-b border-base bg-base px-3 py-2 text-left font-normal text-fg-muted">
                  No product packages yet. Create one under Packages.
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedStores.map((store, index) => {
              const ruleName = store.replenishmentRuleId
                ? (ruleNameById.get(store.replenishmentRuleId) ?? null)
                : null
              const hasActivePackage = visiblePackages.some(
                (pkg) => pkg.status === "active" && pkg.storeIds.includes(store.id),
              )
              const needsSetup = ruleName === null || !hasActivePackage
              const firstOfSource =
                index === 0 || sortedStores[index - 1]?.warehouseId !== store.warehouseId
              return (
                <tr key={store.id}>
                  <td className="sticky left-0 z-20 border-r border-b border-base bg-base px-3 py-1.5 align-top whitespace-nowrap">
                    {firstOfSource && (
                      <span className="paragraph-xs font-medium text-fg-subtle">
                        WH {store.warehouseId ?? "—"}
                      </span>
                    )}
                  </td>
                  <td className="sticky left-32 z-10 border-r border-b border-base bg-base px-3 py-1.5 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <span className="paragraph-xs font-medium">{store.storeNumber}</span>
                      {needsSetup && (
                        <WarningCircle
                          size={12}
                          weight="fill"
                          className="shrink-0 text-badge-orange-fg-text"
                        />
                      )}
                    </div>
                    <div className="paragraph-xs text-fg-muted">
                      {store.classification} · transit {store.leadTimeDays} d ·{" "}
                      {ruleName ?? "no rule"}
                    </div>
                  </td>
                  {visiblePackages.map((pkg) => {
                    const assigned = pkg.storeIds.includes(store.id)
                    const changed = assigned !== draft.isSaved(pkg.id, store.id)
                    const active = assigned && pkg.status === "active"
                    return (
                      <td
                        key={pkg.id}
                        onClick={() => toggleAssignment(store, pkg)}
                        title={
                          pkg.storesFolderId !== null
                            ? "This package follows its folder's stores. Change them on the folder, or give the package its own set."
                            : undefined
                        }
                        className={cn(
                          "border-r border-b border-base px-3 py-1.5 align-top",
                          canEdit &&
                            pkg.storesFolderId === null &&
                            "cursor-pointer hover:bg-subtle",
                          assigned && "bg-highlight",
                          changed && "bg-adjusted-bg",
                        )}
                      >
                        {assigned ? (
                          <>
                            <div className="flex items-center gap-1.5">
                              <span
                                className={cn(
                                  "size-1.5 shrink-0 rounded-full",
                                  active
                                    ? "bg-badge-status-sqaure-success"
                                    : "bg-badge-status-sqaure-information",
                                )}
                              />
                              <span className="paragraph-xs font-semibold">
                                {active ? "Active" : "Upcoming"}
                              </span>
                            </div>
                            <div
                              className={cn(
                                "paragraph-xs truncate",
                                ruleName ? "text-fg-subtle" : "text-badge-orange-fg-text",
                              )}
                            >
                              {ruleName ?? "no rule — no quantities"}
                            </div>
                          </>
                        ) : (
                          <span className="paragraph-xs text-fg-muted">—</span>
                        )}
                      </td>
                    )
                  })}
                  {visiblePackages.length === 0 && <td className="border-b border-base" />}
                </tr>
              )
            })}
            {sortedStores.length === 0 && (
              <tr>
                <td
                  colSpan={2 + visiblePackages.length}
                  className="paragraph-xs px-3 py-6 text-center text-fg-muted"
                >
                  {gapsOnly
                    ? "No stores with gaps."
                    : "No stores yet. Stores load with the retail location data."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <PackageDraftCommandbar />
    </div>
  )
}
